import React, { useState, useCallback } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Row,
  Col,
  Card,
  CardBody,
} from "reactstrap";
import PieChart from "../../Components/Common/PieChart";
import PaymentAccordion from "../../Components/Common/PaymentAccordion";
import { useNavigate } from "react-router-dom";


const UnitChart = React.memo(({ settlementPrice, unitPayments }) => {
  const settlementBalance = settlementPrice?.settled_price || 0;
  const paymentAmounts = unitPayments.map((payment) =>
    parseFloat(payment.amount || 0)
  );

  return (
    <div className="w-100 h-100 position-relative">
      <PieChart
        payments={unitPayments}
        paymentAmounts={paymentAmounts}
        settlementBalance={settlementBalance}
      />
    </div>
  );
});

const ContractUnits = ({ units = [] }) => {
  const navigate = useNavigate();
  const [openAccordion, setOpenAccordion] = useState("");

  const toggle = useCallback((id) => {
    setOpenAccordion(prevOpen => prevOpen === id ? "" : id);

    if (id && openAccordion !== id) {
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          const accordionHeader = element.querySelector(".accordion-header");
          const headerHeight = accordionHeader?.offsetHeight || 0;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition =
            elementPosition + window.pageYOffset - headerHeight;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 300);
    }
  }, [openAccordion]);

  const handleClickGoToUnit = useCallback((e, unitId) => {
    e.stopPropagation();
    navigate(`/unit-details/${unitId}`);
  }, [navigate]);

  const renderContent = () => {
    if (!units || units.length === 0) {
      return (
        <div className="text-center py-4 text-muted">
          <i className="ri-building-line fs-3 mb-2 d-block"></i>
          No units found for this contract
        </div>
      );
    }

    return units.map((unit) => {
      const settlementPrice = unit.settlement_price || {};
      const unitPayments = settlementPrice.payments || [];
      const accordionId = `unit-${unit.id}`;
      
      const unitInfo = {
        id: unit.id,
        nr: unit.nr,
        unit_type: unit.type, 
        block_name: unit.block,
        settled_price: settlementPrice.settled_price || unit.initial_price,
        extras: unit.extras || 0,
      };

      return (
        <AccordionItem key={accordionId} id={accordionId}>
          <AccordionHeader targetId={accordionId}>
            <div className="d-flex justify-content-between align-items-center w-100">
              <div className="d-flex flex-column align-items-start">
                <span className="fw-bold">{`Unit: ${unit.nr}`}</span>
                <span className="fs-sm text-muted">
                  {`Block: ${unit.block}`} |{" "}
                  {`Type: ${unit.type}`}
                </span>
                <span className="fw-bold">
                  €{Intl.NumberFormat("en-US").format(settlementPrice.settled_price || unit.initial_price)}
                </span>
              </div>
              <div
                className="btn btn-outline-secondary btn-sm"
                style={{ marginRight: "10px" }}
                onClick={(e) => handleClickGoToUnit(e, unit.id)}
              >
                View Unit
              </div>
            </div>
          </AccordionHeader>
          <AccordionBody accordionId={accordionId}>
            {openAccordion === accordionId && (
              <Card className="border-0 shadow-none">
                <CardBody>
                  <Row className="g-2 mt-0">
                    <Col lg="7" className="mb-3 mb-md-0">
                      <Card className="border h-100 dashboard-card">
                        <CardBody className="d-flex flex-column">
                          <PaymentAccordion
                            payments={unitPayments}
                            settlementPrice={settlementPrice}
                            unitInfo={unitInfo}
                            aggregated={false}
                          />
                        </CardBody>
                      </Card>
                    </Col>
                    <Col lg="5" className="d-flex justify-content-center">
                      <Card className="border h-100 dashboard-card">
                        <CardBody className="d-flex flex-column">
                          <h5 className="card-title mb-3 text-dark fw-semibold">
                            Payment Distribution
                          </h5>
                          <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                            {unitPayments.length > 0 ? (
                              <UnitChart
                                key={`chart-${settlementPrice.id}`}
                                settlementPrice={settlementPrice}
                                unitPayments={unitPayments}
                              />
                            ) : (
                              <div className="text-center text-muted">
                                <i className="ri-money-dollar-circle-line fs-3 mb-2 d-block"></i>
                                No payment data available
                              </div>
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            )}
          </AccordionBody>
        </AccordionItem>
      );
    });
  };

  return (
    <Accordion open={openAccordion} toggle={toggle} className="mb-3">
      {renderContent()}
    </Accordion>
  );
};

export default React.memo(ContractUnits);